import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {projectDocumentsApi} from '../api';
import {ProjectDocumentsFilter, ProjectDocumentsResponse, CreateProjectDocumentDTO, ProjectDocumentModel} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type ProjectDocumentsState = {
  getInfiniteProjectDocuments: UseInfiniteQueryResult<InfiniteData<ProjectDocumentsResponse>, Error>;
  filters: ProjectDocumentsFilter;
  updateFilter: (newFilter: Partial<ProjectDocumentsFilter>) => void;
  createProjectDocumentMutation: UseMutationResult<ProjectDocumentModel, Error, CreateProjectDocumentDTO>;
  deleteProjectDocumentMutation: UseMutationResult<void, Error, string>;
};

const DEFAULT_PAGE_SIZE = 25;

export const useProjectDocuments = (initialFilter: ProjectDocumentsFilter = {}): ProjectDocumentsState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ProjectDocumentsFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteProjectDocuments = useInfiniteQuery<
    ProjectDocumentsResponse,
    Error,
    InfiniteData<ProjectDocumentsResponse>,
    ['projectDocuments', 'infinite', Omit<ProjectDocumentsFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['projectDocuments', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return projectDocumentsApi.getProjectDocuments({
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<ProjectDocumentsFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  const createProjectDocumentMutation = useMutation({
    mutationFn: projectDocumentsApi.createProjectDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['projectDocuments', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Документ успешно создан',
        name: 'createProjectDocument',
        theme: 'success',
      });
    },
  });

  const deleteProjectDocumentMutation = useMutation({
    mutationFn: projectDocumentsApi.deleteProjectDocument,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['projectDocuments', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Документ успешно удален',
        name: 'deleteProjectDocument',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteProjectDocuments,
    filters,
    updateFilter,
    createProjectDocumentMutation,
    deleteProjectDocumentMutation,
  };
};

